import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDocumentCreationContext } from "@contexts";
import {
  useRequestData,
  usePersistentCounter,
  useRequestSigningStatus,
  useDoctorDocumentReadiness,
} from "@hooks";
import {
  CompoundedPrescriptionDetails,
  Patient,
  RequestStatus,
  RequestType,
  patientDefaultValues,
} from "@interfaces";
import {
  Body,
  FullScreenLoader,
  Header,
  PatientSummaryAccordion,
  PatientSummary as BasePatientSummary,
  NavigateBeforeButton,
  ContentContainer as BaseContentContainer,
  DigitalSign,
  DoctorDocumentReadinessModal,
} from "@components";
import { MedicineTreatmentForm } from "../PrescriptionForm/components/MedicineTreatmentForm";
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  saveToLocalStorage,
} from "@utils";
import {
  Box,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";

const ContentContainer = styled(BaseContentContainer)(({ theme }) => ({
  gap: 24,
}));

const PatientSummary = styled(BasePatientSummary)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const HeaderContainer = styled(Body)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  width: "100%",
  paddingBottom: 0,
  justifyContent: "center",
  [theme.breakpoints.up("md")]: {
    justifyContent: "space-between",
    padding: 24,
    paddingBottom: 0,
    maxWidth: 1200,
  },
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const Content = styled(Body)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  width: "100%",
  alignItems: "normal",
  [theme.breakpoints.up("md")]: {
    padding: 24,
    maxWidth: 1200,
    paddingTop: 0,
    paddingBottom: 0,
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "center",
    paddingTop: 0,
  },
}));

const PatientContent = styled(Box)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("md")]: {
    maxWidth: 328,
  },
}));

const PrescriptionContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  width: "100%",
  paddingTop: 0,
}));

const PatientSummaryAccordionContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("md")]: {
    display: "none",
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

export function CompoundedPrescriptionCreate() {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const { requestId = "" } = useParams();
  const { data: requestData, isPending: isPendingRequestData } =
    useRequestData();
  const { isSigning } = useRequestSigningStatus(requestData?.request.status);

  const [showDigitalSign, setShowDigitalSign] = useState<boolean>(false);
  const { ensureReadyToIssue, modalProps } = useDoctorDocumentReadiness({
    requestId,
    stopBlockedOnOpen: true,
  });
  const [compoundedPrescriptionDetails, setCompoundedPrescriptionDetails] =
    useState<CompoundedPrescriptionDetails | null>(null);

  const { triggerCreateCompoundedPrescription } = useDocumentCreationContext();

  const patient: Patient = useMemo(() => {
    return requestData?.patient ?? patientDefaultValues;
  }, [requestData]);

  const isDraft = requestData?.request.status === RequestStatus.DRAFT;

  const handleBack = useCallback(() => {
    if (isDraft) {
      navigate(`/app/nuevo-documento/paciente`, {
        state: { documentType: RequestType.COMPOUNDED_PRESCRIPTION },
      });
    } else {
      navigate(`/app/solicitudes/${requestId}/gestionar/seleccionar-documento`);
    }
    removeFromLocalStorage("requestType");
    removeFromLocalStorage("compoundedPrescriptionDetails");
  }, [navigate, isDraft, requestId]);

  const onDigitalSignSubmitFEA = useCallback(() => {
    setShowDigitalSign(false);
    if (!requestData || !compoundedPrescriptionDetails) return;

    triggerCreateCompoundedPrescription({
      requestId,
      compoundedPrescriptionDetails,
    }).catch((error) => {
      console.error("Error creating compounded prescription:", error);
    });

    navigate(`/app/solicitudes/${requestId}/receta-magistral/firma`);
  }, [
    requestData,
    requestId,
    compoundedPrescriptionDetails,
    triggerCreateCompoundedPrescription,
    navigate,
  ]);

  const handleSubmit = useCallback(
    async (formData: CompoundedPrescriptionDetails) => {
      setCompoundedPrescriptionDetails(formData);
      saveToLocalStorage({
        key: "requestType",
        value: RequestType.COMPOUNDED_PRESCRIPTION,
      });
      saveToLocalStorage({
        key: "compoundedPrescriptionDetails",
        value: formData,
        stringifyJson: true,
      });
      ensureReadyToIssue(() => {
        setShowDigitalSign(true);
      });
    },
    [ensureReadyToIssue]
  );

  // Reset unknown error retries when creating a new prescription
  const [, , resetUnknownErrorRetries] = usePersistentCounter(
    "unknownSignatureErrorRetries"
  );
  useEffect(() => {
    resetUnknownErrorRetries();
  }, [resetUnknownErrorRetries]);

  useEffect(() => {
    if (isSigning) {
      navigate("/app/solicitudes");
      enqueueSnackbar("La receta magistral ya está en proceso de firma", {
        variant: "info",
      });
    }
  }, [isSigning, navigate, enqueueSnackbar]);

  if (showDigitalSign) {
    return (
      <DigitalSign
        documentType={RequestType.COMPOUNDED_PRESCRIPTION}
        handleCreateDocument={onDigitalSignSubmitFEA}
        handleBack={() => setShowDigitalSign(false)}
      />
    );
  }

  if (isPendingRequestData) {
    return (
      <>
        <Header
          title={isMobile ? "Receta magistral" : undefined}
          useMekiDocIcon={!isMobile}
          titleColor="black"
          left={<NavigateBeforeButton onClick={handleBack} />}
        />
        <ContentContainer flex={1} disableGutters>
          <Body justifyContent="center">
            <FullScreenLoader title="Cargando información del paciente..." />
          </Body>
        </ContentContainer>
      </>
    );
  }

  return (
    <>
      <Header
        title={isMobile ? "Receta magistral" : undefined}
        useMekiDocIcon={!isMobile}
        titleColor="black"
        left={<NavigateBeforeButton onClick={handleBack} />}
      />
      <ContentContainer disableGutters>
        <HeaderContainer>
          <Title variant="h4" fontWeight={"medium"}>
            Receta magistral
          </Title>
        </HeaderContainer>
        <Content>
          <PatientContent>
            <PatientSummary
              patient={patient}
              prevRoute={`/app/solicitudes/${requestId}/receta-magistral/crear`}
              requestId={requestId}
              canEdit={isDraft}
            />
            <PatientSummaryAccordionContainer>
              <PatientSummaryAccordion
                patient={patient}
                prevRoute={`/app/solicitudes/${requestId}/receta-magistral/crear`}
                requestId={requestId}
                isOpen={isDraft}
                canEdit={isDraft}
              />
            </PatientSummaryAccordionContainer>
          </PatientContent>
          <PrescriptionContent>
            <MedicineTreatmentForm
              prefilledMedicineAttributes={
                location.state ||
                getFromLocalStorage({
                  key: "compoundedPrescriptionDetails",
                  parseJson: true,
                }) ||
                {}
              }
              formManagement={{
                showCompoundedIngredients: true,
                onSubmit: handleSubmit,
                isLoading: false,
              }}
            />
          </PrescriptionContent>
        </Content>
      </ContentContainer>
      <DoctorDocumentReadinessModal {...modalProps} />
    </>
  );
}
